import { Component, signal, effect, OnInit, OnDestroy, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';

import { SidebarComponent } from './shared/components/sidebar';
import { TopbarComponent } from './shared/components/topbar/topbar.component';
import { ProfileSettingsModalComponent } from './shared/components/profile-settings-modal/profile-settings-modal.component';
import { ChatBuddyComponent } from './features/ai-chat/chat-buddy/chat-buddy.component';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthService } from './core/services/auth.service';
import { Subscription } from 'rxjs';
import { StorageService } from './core/services/storage.service';
import { STORAGE } from './shared/constants/storage.constants';
import { SidebarService } from './core/services/sidebar.service';
import { NavigationGuardService } from './core/services/navigation-guard.service';
import { BrandingService } from './core/services/branding.service';
import { PermissionService } from './core/services/permission.service';
import { TenantRouteSyncService } from './core/services/tenant-route-sync.service';
import { AdminPortalReturnUrlService } from './core/services/admin-portal-return-url.service';
import { NotificationsPollService } from './core/services/notifications-poll.service';
import { getTenantIdFromRouter, isNotificationReplyRoute } from './core/utils/tenant-route.util';
import { AioToastOutletComponent } from '@aio/ui';
import { ViewportService } from './core/services/viewport.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    SidebarComponent,
    TopbarComponent,
    ProfileSettingsModalComponent,
    AioToastOutletComponent,
    ChatBuddyComponent,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit, OnDestroy {
  protected title = 'aio-login-test';

  // List of routes where sidebar/topbar should be hidden
  private hiddenRoutes = ['/login', '/forgot-password', '/set-new-password', '/create-password', '/mfa', '/reset-success'];
  showLayout = true;
  pageTitle = '';

  // User info for topbar
  userInitials = '';
  userName = '';
  userEmail = '';
  userRole = '';
  userProfilePicture = '';
  showNotifications = false;

  profileSettingsModalOpen = signal(false);

  // Sidebar collapse state
  sidebarCollapsed = false;
  /** Phone width: the sidebar is an off-canvas drawer instead of a docked column. */
  readonly isMobile = inject(ViewportService).isMobile;
  /** Drawer open state at phone width. Transient — never persisted like `sidebarCollapsed`. */
  readonly mobileNavOpen = signal(false);
  private storageService = inject(StorageService);
  private readonly navigationGuardService = inject(NavigationGuardService);
  private readonly brandingService = inject(BrandingService);
  private readonly permissionService = inject(PermissionService);
  private readonly adminPortalReturnUrlService = inject(AdminPortalReturnUrlService);
  private readonly notificationsPollService = inject(NotificationsPollService);

  /** Tracks tenant used for badge polling so workspace switches refresh immediately. */
  private lastNotificationPollTenantId = '';

  // Auth subscription
  private authSubscription?: Subscription;

  // Map route patterns to page titles
  private routeTitleMap: { pattern: RegExp, title: string }[] = [
    { pattern: /^\/login/, title: '' },
    { pattern: /^\/forgot-password/, title: '' },
    { pattern: /^\/set-new-password/, title: '' },
    { pattern: /^\/create-password/, title: '' },
    { pattern: /^\/mfa/, title: '' },
    { pattern: /^\/[\w-]+\/task-management/, title: 'Workflows' },
    { pattern: /^\/[\w-]+\/leads/, title: '' }, // Empty title for leads since it's the first page
    { pattern: /^\/[\w-]+\/opportunities/, title: 'Opportunities' },
    { pattern: /^\/[\w-]+\/contacts/, title: 'Contacts' },
    { pattern: /^\/[\w-]+\/insights/, title: 'Insights' },
    { pattern: /^\/[\w-]+\/time-clock/, title: 'Time Clock' },
    { pattern: /^\/[\w-]+\/finance\/cost-inbox/, title: 'Cost Inbox' },
    { pattern: /^\/[\w-]+\/admin\/users/, title: 'Users' },
    { pattern: /^\/[\w-]+\/admin\/roles/, title: 'Roles' },
    { pattern: /^\/[\w-]+\/admin\/integrations/, title: 'Integrations' },
    { pattern: /^\/[\w-]+\/admin/, title: 'Admin' },
    { pattern: /^\/[\w-]+\/profile/, title: 'Profile' },
    { pattern: /^\/[\w-]+\/task-management\/taskboard/, title: 'Taskboard' },
    { pattern: /^\/[\w-]+\/estimating\/board/, title: 'Template' },
    { pattern: /^\/[\w-]+\/estimating/, title: 'Templates' },
    { pattern: /^\/reset-success/, title: '' },
    // Add more as needed
  ];

  constructor(private router: Router, private authService: AuthService, private sidebarService: SidebarService) {
    inject(TenantRouteSyncService);

    this.loadSidebarState();
    this.showLayout = this.shouldShowLayout(this.router.url);

    // Leaving phone width drops the drawer so the docked column takes over cleanly.
    effect(() => {
      if (!this.isMobile()) {
        this.mobileNavOpen.set(false);
      }
    });

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.adminPortalReturnUrlService.recordNavigationIfApplicable(event.urlAfterRedirects);
      this.showLayout = this.shouldShowLayout(event.urlAfterRedirects);
      this.mobileNavOpen.set(false);
      this.syncNotificationPollForSession();
      this.updatePageTitle(event.urlAfterRedirects);
      // Breadcrumbs will be handled by individual page components
      this.setUserInfo();
    });



    this.setUserInfo();
  }

  private shouldShowLayout(url: string): boolean {
    return (
      !this.hiddenRoutes.some((route) => url.startsWith(route)) &&
      !isNotificationReplyRoute(url)
    );
  }

  ngOnInit(): void {
    // Subscribe to auth state changes to update user info when it changes
    this.authSubscription = this.authService.authState$.subscribe(authState => {
      // Always update user info when auth state changes, regardless of authentication status
      this.setUserInfo();
    });
  }

  ngOnDestroy(): void {
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
    this.notificationsPollService.stop();
  }

  private getPageTitle(url: string): string {
    if (/\/[^/]+\/home\/?($|\?)/.test(url)) {
      return '';
    }

    // More specific matching for task management
    if (url.includes('task-management')) {
      return 'Task Management';
    }

    // More specific matching for opportunities
    if (url.includes('opportunities')) {
      return 'Opportunities';
    }

    // More specific matching for leads
    if (url.includes('leads')) {
      return ''; // Return empty string for leads since it's the first page
    }

    if (url.includes('time-clock')) {
      return 'Time Clock';
    }

    for (const entry of this.routeTitleMap) {
      if (entry.pattern.test(url)) {
        return entry.title;
      }
    }
    return '';
  }

  // Force update page title when navigating
  private updatePageTitle(url: string): void {
    const newTitle = this.getPageTitle(url);
    this.pageTitle = newTitle;
  }



  /** Start badge polling only when authenticated with a resolvable tenant (route or stored). */
  private syncNotificationPollForSession(): void {
    if (isNotificationReplyRoute(this.router.url)) {
      this.notificationsPollService.stop();
      return;
    }

    if (!this.authService.getCurrentUser()) {
      this.lastNotificationPollTenantId = '';
      this.notificationsPollService.stop();
      return;
    }

    let tenantId = getTenantIdFromRouter(this.router);
    if (!tenantId) {
      tenantId = this.authService.getStoredTenantId() ?? '';
    }

    if (tenantId) {
      const tenantChanged =
        this.lastNotificationPollTenantId !== '' &&
        this.lastNotificationPollTenantId !== tenantId;
      this.lastNotificationPollTenantId = tenantId;

      if (tenantChanged) {
        this.notificationsPollService.onTenantContextChanged();
      } else {
        this.notificationsPollService.start();
      }
    } else {
      this.lastNotificationPollTenantId = '';
      this.notificationsPollService.stop();
    }
  }

  private setUserInfo() {
    const user = this.authService.getCurrentUser();
    if (user) {
      this.userInitials = user.firstName && user.lastName ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase() : (user.email ? user.email[0].toUpperCase() : 'U');
      this.userName = user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : (user.email || 'Unknown User');
      this.userEmail = user.email || '';
      this.userProfilePicture = user.profilePictureUrl || '';
      this.showNotifications = true;
      this.syncNotificationPollForSession();
      if (user.roleName?.trim()) {
        this.userRole = user.roleName.trim();
      } else if (user.globalRoles && user.globalRoles.length > 0) {
        const role = user.globalRoles[0];
        this.userRole = typeof role === 'string' ? role : (role.name || 'User');
      } else {
        this.userRole = 'User';
      }
      // Refresh: permissions were loaded in a prior session (no new API call); still load branding once.
      if (this.permissionService.isCached()) {
        this.brandingService.refreshGlobalBrandingFromAuth();
      }
    } else {
      this.userInitials = '';
      this.userName = '';
      this.userEmail = '';
      this.userRole = '';
      this.userProfilePicture = '';
      this.showNotifications = false;
      this.lastNotificationPollTenantId = '';
      this.notificationsPollService.stop();
      this.brandingService.clearGlobalBranding();
    }
  }

  private loadSidebarState(): void {
    const savedState = this.storageService.get<boolean>(STORAGE.SIDEBAR_COLLAPSED);
    if (savedState !== null) {
      this.sidebarCollapsed = savedState;
    }

    // Notify the sidebar service of the initial state
    this.sidebarService.updateSidebarState(this.sidebarCollapsed);
  }

  toggleSidebar() {
    if (this.isMobile()) {
      this.mobileNavOpen.update((open) => !open);
      return;
    }
    this.sidebarCollapsed = !this.sidebarCollapsed;
    this.saveSidebarState();

    // Notify the sidebar service of the state change
    this.sidebarService.updateSidebarState(this.sidebarCollapsed);
  }

  closeMobileNav(): void {
    this.mobileNavOpen.set(false);
  }

  /** Topbar hamburger state: at phone width it mirrors the drawer, otherwise the docked column. */
  topbarCollapsed(): boolean {
    return this.isMobile() ? !this.mobileNavOpen() : this.sidebarCollapsed;
  }

  private saveSidebarState(): void {
    this.storageService.set(STORAGE.SIDEBAR_COLLAPSED, this.sidebarCollapsed);
  }

  signOut() {
    this.authService.logout().subscribe(() => {
      this.adminPortalReturnUrlService.clear();
      this.router.navigate(['/login']);
    });
  }

  profileSettings() {
    const currentUrl = this.router.url;
    const tenantMatch = currentUrl.match(/\/([^\/]+)\//);
    const tenantId = tenantMatch ? tenantMatch[1] : '';

    if (!tenantId) {
      this.router.navigate(['/login']);
      return;
    }
    this.profileSettingsModalOpen.set(true);
  }

  closeProfileSettingsModal(): void {
    this.profileSettingsModalOpen.set(false);
  }

  adminPortal() {
    const currentUrl = this.router.url;
    const tenantMatch = currentUrl.match(/\/([^\/]+)\//);
    const tenantId = tenantMatch ? tenantMatch[1] : '';

    if (tenantId) {
      const route = [`/${tenantId}/admin/users`];
      if (this.navigationGuardService.attemptNavigation(route)) {
        this.router.navigate(route);
      }
    } else if (this.navigationGuardService.attemptNavigation(['/login'])) {
      this.router.navigate(['/login']);
    }
  }




}
